import { useState } from 'react';

const HelpfulResources = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const resources = [
    {
      id: 'faq',
      title: 'Frequently Asked Questions',
      icon: '❓',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">How long does the review process take?</h4>
            <p className="text-sm text-gray-600">
              Typically, applications are reviewed within 5-7 business days. You'll be notified via email when there's an update.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">What documents do I need?</h4>
            <p className="text-sm text-gray-600">
              Required documents include: ID copy, business license, financial statements, and bank statements. Additional documents may be requested during review.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Can I modify my application?</h4>
            <p className="text-sm text-gray-600">
              Once submitted, applications cannot be modified. However, you can upload additional supporting documents through this portal.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'contact',
      title: 'Contact Information',
      icon: '📞',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Phone Support</h4>
            <p className="text-sm text-gray-600">
              <strong>Phone:</strong> +263-71-708-2807<br />
              <strong>Hours:</strong> Monday - Friday, 8:00 AM - 5:00 PM
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Email Support</h4>
            <p className="text-sm text-gray-600">
              <strong>Email:</strong> info@gandimall.com<br />
              <strong>Response Time:</strong> Within 24 hours
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Physical Address</h4>
            <p className="text-sm text-gray-600">
              The Gandi Mall<br />
              33 Magamba Way, Chinhoyi
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'tips',
      title: 'Application Tips',
      icon: '💡',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Complete Documentation</h4>
            <p className="text-sm text-gray-600">
              Ensure all required documents are clear, legible, and up-to-date. Blurry or incomplete documents may delay the review process.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Business Plan</h4>
            <p className="text-sm text-gray-600">
              Provide a detailed business plan that clearly explains your business model, target market, and expected revenue.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Financial Stability</h4>
            <p className="text-sm text-gray-600">
              Demonstrate financial stability through bank statements, financial projections, and proof of sufficient capital.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'next-steps',
      title: 'What Happens Next?',
      icon: '🚀',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">After Approval</h4>
            <p className="text-sm text-gray-600">
              If approved, you'll receive a lease agreement to review and sign. You'll also be provided with move-in instructions and key collection details.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Lease Terms</h4>
            <p className="text-sm text-gray-600">
              Standard lease terms include monthly rent, security deposit, and maintenance responsibilities. All terms will be clearly outlined in your lease agreement.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Move-in Process</h4>
            <p className="text-sm text-gray-600">
              Once the lease is signed, you'll coordinate with our team to schedule your move-in date and complete any necessary setup.
            </p>
          </div>
        </div>
      )
    }
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Helpful Resources</h3>
      
      <div className="space-y-3">
        {resources.map((resource) => (
          <div key={resource.id} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection(resource.id)}
              className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
              <div className="flex items-center space-x-3">
                <span className="text-lg">{resource.icon}</span>
                <span className="font-medium text-gray-900">{resource.title}</span>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transform transition-transform ${
                  expandedSection === resource.id ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {expandedSection === resource.id && (
              <div className="px-4 pb-4 border-t border-gray-200">
                <div className="pt-4">
                  {resource.content}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">Need More Help?</h4>
            <p className="text-sm text-blue-700 mt-1">
              If you have questions not covered here, feel free to send us a message through the messaging system or contact us directly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpfulResources;
